/**
 * Slim page fragment for lazy-loading region blocks.
 * Requests __typename, sys.id, and jump-link fields (when available) for mainRegionCollection
 * items so the initial page query stays under Contentful's 8KB limit. Each block then
 * fetches its full data via CONTENTFUL_METADATA hooks in Component (useXById).
 *
 * jumpLink and jumpLinkMarginTop (KL-1058) are included for destination modules so
 * anchor IDs and offsets are available before the full entry fetch completes.
 *
 * Lives in king-living so it survives `yarn contentful generate` (which overwrites pwa-contentful).
 * Keep union members in sync with pwa-contentful page.fragment.js.
 */

import gql from 'graphql-tag';

import {BasePageFragment} from '@amblique/king-living/app/contentful/fragments/page/base-page.fragment';
import {BaseSeoFragment} from '@amblique/contentful/app/contentful/fragments/seo/base-seo.fragment';

export const PageFragmentSlim = gql`
  ${BasePageFragment}
  ${BaseSeoFragment}

  fragment PageFragmentSlim on Page {
    ...BasePageFragment

    mainRegionCollection(limit: 25) {
      items {
        __typename
        ... on Accordion {
          sys {
            id
          }
        }
        ... on AccordionPanel {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Blog {
          sys {
            id
          }
        }
        ... on BlogGrid {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on BlogListing {
          sys {
            id
          }
        }
        ... on BlogRelatedArticles {
          sys {
            id
          }
        }
        ... on Breadcrumb {
          sys {
            id
          }
        }
        ... on Card {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on CategoryCardGrid {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Carousel {
          sys {
            id
          }
        }
        ... on ProductTiles {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Container {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on DescriptionBlock {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on EmbedPanel {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on FeatureList {
          sys {
            id
          }
        }
        ... on FeaturedInWithLogosCf052 {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on HeroBanner {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on IconSet {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Image {
          sys {
            id
          }
        }
        ... on KingDifferenceMediaCf013 {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Link {
          sys {
            id
          }
        }
        ... on LoopingTicker {
          sys {
            id
          }
        }
        ... on MediaCarousel {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on MediaContentSection {
          sys {
            id
          }
        }
        ... on MediaWithScrollableText {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Reviews {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on RichTextBlock {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on SectionPanel {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on ShopTheLook {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on ShowroomDetails {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on SplitHeading {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on SplitPanelDoubleImageBlock {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on TeaserTileBlock {
          sys {
            id
          }
          jumpLink
          jumpLinkMarginTop
        }
        ... on Video {
          sys {
            id
          }
        }
      }
    }
    seo {
      ...BaseSeoFragment
    }
  }
`;
